import React, { useEffect, useRef, useState } from 'react';

/**
 * Viator (TripAdvisor) attractions widget, usable from page content /
 * column content via JsxParser:
 *
 *   <ViatorWidget partnerId="P00256778" widgetRef="W-f351a28c-..." />
 *
 * The raw Viator embed code cannot work in content fields — JsxParser
 * strips <script> tags — so this component renders the widget div and
 * loads Viator's script itself.
 *
 * Viator is a third party: its script sets cookies for affiliate
 * attribution and receives the visitor's IP. The sites' privacy policy
 * promises no third-party cookies without a choice, so the widget is
 * click-to-load: the visitor taps once to load it, and that choice is
 * remembered per browser (aw_viator_ok) so it loads automatically from
 * then on. Pass auto to skip the gate (only if policy/consent handled
 * some other way).
 */

const VIATOR_SRC = 'https://www.viator.com/orion/partner/widget.js';
export const VIATOR_CONSENT_KEY = 'aw_viator_ok';

// Shared with ViatorBanner: one consent covers all Viator content.
export const readViatorConsent = () => {
  try { return localStorage.getItem(VIATOR_CONSENT_KEY) === '1'; } catch (e) { return false; }
};
export const grantViatorConsent = () => {
  try { localStorage.setItem(VIATOR_CONSENT_KEY, '1'); } catch (e) { /* private mode */ }
};
const readConsent = readViatorConsent;

// Values land inside iframe srcdoc markup — allow only id-safe characters.
export const safeAttr = (v) => String(v || '').replace(/[^A-Za-z0-9_-]/g, '');
const safe = safeAttr;

const ViatorWidget = ({ partnerId, widgetRef, auto = false, height = 320 }) => {
  const [loaded, setLoaded] = useState(() => auto || readConsent());
  const frameRef = useRef(null);

  // Auto-size the frame to the widget's content so the page has no inner
  // scrollbar. srcdoc frames are same-origin, so the height is readable;
  // polled because the widget loads (and re-sizes) asynchronously.
  useEffect(() => {
    if (!loaded) return undefined;
    const timer = setInterval(() => {
      const frame = frameRef.current;
      if (!frame) return;
      try {
        const body = frame.contentDocument && frame.contentDocument.body;
        if (!body) return;
        const h = body.scrollHeight;
        if (h > 60 && Math.abs(h - frame.offsetHeight) > 4) {
          frame.style.height = h + 'px';
        }
      } catch (e) { /* frame not ready */ }
    }, 400);
    return () => clearInterval(timer);
  }, [loaded]);

  if (!partnerId || !widgetRef) return null;

  if (!loaded) {
    return (
      <div className="aw-viator-gate">
        <div className="aw-viator-gate-icon" aria-hidden="true">🎟️</div>
        <p className="aw-viator-gate-title">Tours &amp; attractions</p>
        <p className="aw-viator-gate-sub">
          Days out, sights and experiences near and far &mdash; with prices,
          reviews and free cancellation.
        </p>
        <button
          type="button"
          className="aw-websearch-ddg-btn aw-viator-btn"
          onClick={() => {
            try { localStorage.setItem(VIATOR_CONSENT_KEY, '1'); } catch (e) { /* private mode */ }
            setLoaded(true);
          }}
        >
          Show attractions
        </button>
        <p className="aw-viator-gate-note">
          Provided by Viator, a TripAdvisor company. Loading connects your
          browser to Viator, which may set cookies.
        </p>
      </div>
    );
  }

  // The widget lives in its own iframe: viator's widget.js only scans
  // for widget divs once per document and refuses to run twice, which
  // goes blank when the SPA re-renders (e.g. cached data replaced by a
  // fresh API response on reload). A fresh srcdoc document per mount
  // makes every load a first load.
  const doc =
    '<!doctype html><html><head><meta charset="utf-8">' +
    '<style>html,body{margin:0;padding:0;overflow:hidden}</style></head><body>' +
    '<div data-vi-partner-id="' + safe(partnerId) + '" data-vi-widget-ref="' + safe(widgetRef) + '"></div>' +
    '<script async src="' + VIATOR_SRC + '"><' + '/script>' +
    '</body></html>';

  return (
    <iframe
      ref={frameRef}
      className="aw-viator"
      title="Tours and attractions from Viator"
      srcDoc={doc}
      scrolling="no"
      style={{ width: '100%', height: Number(height) || 320, border: 0, display: 'block' }}
      loading="lazy"
    />
  );
};

export default ViatorWidget;
